/**
 * Recruitment API client (ADR-0012). Wraps /v1/recruitment/* — requisitions
 * (with the Phase 2.1 open-requisition approval gate), candidates + CV + CSV
 * import (Phase 3), applications/pipeline, offers (with the extend-offer gate)
 * + the convert-to-Employee seam, and the candidate-PII retention queue (D-9).
 */
import { apiRequest, apiDownload } from './client.js';

// ── Enums ────────────────────────────────────────────────────────────────────
export type RequisitionStatus = 'draft' | 'pending_open_approval' | 'open' | 'on_hold' | 'filled' | 'closed';
export type JobApplicationStatus = 'applied' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected' | 'withdrawn';
export type JobOfferStatus = 'draft' | 'approved' | 'pending_extend_approval' | 'extended' | 'accepted' | 'declined';

/** The funnel statuses shown as pipeline-board columns (terminal states aside). */
export const PIPELINE_STATUSES: JobApplicationStatus[] = ['applied', 'screening', 'interview', 'offer', 'hired', 'rejected', 'withdrawn'];

/** Candidate CV content-type allow-list — surfaced in the upload UI (D-8). */
export const CV_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
export const CV_ALLOWED_LABEL = 'PDF, Word (.doc/.docx), or image (JPG/PNG)';

// ── Shapes ───────────────────────────────────────────────────────────────────
export interface ResolvedApprover {
  approverUserId: string | null;
  approverEmployeeId: string | null;
  level: 'direct_manager' | 'grand_manager' | 'hr_fallback' | string;
}

export interface Requisition {
  id: string;
  title: string;
  department: string | null;
  headcount: number;
  status: RequisitionStatus;
  hiringManagerId: string | null;
  openApproverUserId: string | null;
  resolvedApprover?: ResolvedApprover;
  createdAt: string;
}

export interface Candidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  countryCode: string;
  source: string | null;
  consentGivenAt: string | null;
  retentionUntil: string | null;
  anonymizedAt: string | null;
  createdAt: string;
}

export interface Application {
  id: string;
  candidateId: string;
  requisitionId: string;
  stageId: string | null;
  status: JobApplicationStatus;
  notes: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

export interface Offer {
  id: string;
  applicationId: string;
  grossSalary: string;
  currency: string;
  payFrequency: 'monthly' | 'weekly' | 'daily' | 'hourly' | string;
  employeeNumber: string | null;
  status: JobOfferStatus;
  extendApproverUserId: string | null;
  convertedEmployeeId: string | null;
  resolvedApprover?: ResolvedApprover;
  createdAt: string;
}

export interface CvArtifact {
  id: string;
  candidateId: string;
  kind: string;
  label: string | null;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface ApprovalsResponse {
  requisitionOpens: Requisition[];
  offerExtends: Array<Offer & { application: { candidate: { id: string; firstName: string; lastName: string }; requisition: { id: string; title: string } } }>;
}

export interface ImportDisposition {
  row: number;
  disposition: 'will_import' | 'duplicate';
  email: string | null;
}
export interface ImportRowError {
  row: number;
  category: string;
  message: string;
}
export interface ImportResult {
  committed: boolean;
  importedCount?: number;
  totals: { dataRows: number; willImport: number; duplicate: number; errors: number };
  dispositions: ImportDisposition[];
  errors: ImportRowError[];
}

/** The 409 REHIRE_WARNING body (D-4) the convert route returns. */
export interface RehireWarning {
  code: 'REHIRE_WARNING';
  message: string;
  match: { id: string; employeeNumber: string; firstName: string; lastName: string };
}

// ── Requisitions ─────────────────────────────────────────────────────────────
export function listRequisitions(q: { status?: RequisitionStatus } = {}): Promise<{ requisitions: Requisition[] }> {
  const qs = q.status ? `?status=${q.status}` : '';
  return apiRequest(`/v1/recruitment/requisitions${qs}`);
}
export function createRequisition(body: { title: string; department?: string; headcount?: number; hiringManagerId?: string }): Promise<Requisition> {
  return apiRequest('/v1/recruitment/requisitions', { method: 'POST', body });
}
export function transitionRequisition(id: string, body: { status: RequisitionStatus; reason?: string }): Promise<Requisition> {
  return apiRequest(`/v1/recruitment/requisitions/${encodeURIComponent(id)}/transition`, { method: 'POST', body });
}
export function requestOpenRequisition(id: string): Promise<Requisition & { resolvedApprover: ResolvedApprover }> {
  return apiRequest(`/v1/recruitment/requisitions/${encodeURIComponent(id)}/request-open`, { method: 'POST', body: {} });
}
export function approveOpenRequisition(id: string): Promise<Requisition> {
  return apiRequest(`/v1/recruitment/requisitions/${encodeURIComponent(id)}/approve-open`, { method: 'POST', body: {} });
}
export function rejectOpenRequisition(id: string, body: { reason?: string } = {}): Promise<Requisition> {
  return apiRequest(`/v1/recruitment/requisitions/${encodeURIComponent(id)}/reject-open`, { method: 'POST', body });
}

// ── Approver queue (Phase 4) ─────────────────────────────────────────────────
export function listApprovals(view: 'pending_my_approval' | 'all'): Promise<ApprovalsResponse> {
  return apiRequest(`/v1/recruitment/approvals?view=${view}`);
}

// ── Candidates ───────────────────────────────────────────────────────────────
export function listCandidates(q: { email?: string } = {}): Promise<{ candidates: Candidate[] }> {
  const qs = q.email ? `?email=${encodeURIComponent(q.email)}` : '';
  return apiRequest(`/v1/recruitment/candidates${qs}`);
}
export function createCandidate(body: {
  firstName: string; lastName: string; countryCode: string; email?: string; phone?: string; source?: string; consentGiven?: boolean;
}): Promise<Candidate> {
  return apiRequest('/v1/recruitment/candidates', { method: 'POST', body });
}
export function getCandidate(id: string): Promise<Candidate> {
  // No GET-by-id route; the list is small + tenant-scoped. Fetch + find.
  return listCandidates().then((r) => {
    const found = r.candidates.find((c) => c.id === id);
    if (!found) throw new Error('Candidate not found');
    return found;
  });
}
export function anonymizeCandidate(id: string, reason: string): Promise<{ anonymized: boolean; artifactsDeleted: number }> {
  return apiRequest(`/v1/recruitment/candidates/${encodeURIComponent(id)}/anonymize`, { method: 'POST', body: { reason } });
}
export function listPurgeEligible(): Promise<{ candidates: Array<Pick<Candidate, 'id' | 'firstName' | 'lastName' | 'email' | 'countryCode' | 'source' | 'retentionUntil' | 'createdAt'>>; evaluatedAt: string }> {
  return apiRequest('/v1/recruitment/candidates/purge-eligible');
}

// ── CV (D-8) ─────────────────────────────────────────────────────────────────
export function listCvs(candidateId: string): Promise<{ artifacts: CvArtifact[] }> {
  return apiRequest(`/v1/recruitment/candidates/${encodeURIComponent(candidateId)}/cv`);
}
export function uploadCv(candidateId: string, file: File, label?: string): Promise<CvArtifact> {
  const form = new FormData();
  if (label) form.append('metadata', JSON.stringify({ label }));
  form.append('file', file);
  return apiRequest(`/v1/recruitment/candidates/${encodeURIComponent(candidateId)}/cv`, { method: 'POST', body: form });
}
export function downloadCv(candidateId: string, artifactId: string, filename: string): Promise<void> {
  return apiDownload(`/v1/recruitment/candidates/${encodeURIComponent(candidateId)}/cv/${encodeURIComponent(artifactId)}/download`, filename);
}

// ── CSV import (D-10) ────────────────────────────────────────────────────────
export function downloadImportTemplate(): Promise<void> {
  return apiDownload('/v1/recruitment/candidates/import/template', 'lumohr-candidate-import-template.csv');
}
export function importCandidates(file: File, commit: boolean): Promise<ImportResult> {
  const form = new FormData();
  form.append('commit', String(commit));
  form.append('file', file);
  return apiRequest('/v1/recruitment/candidates/import', { method: 'POST', body: form });
}

// ── Applications / pipeline ──────────────────────────────────────────────────
export function listApplications(q: { requisitionId?: string; candidateId?: string; status?: JobApplicationStatus } = {}): Promise<{ applications: Application[] }> {
  const params = new URLSearchParams();
  if (q.requisitionId) params.set('requisitionId', q.requisitionId);
  if (q.candidateId) params.set('candidateId', q.candidateId);
  if (q.status) params.set('status', q.status);
  const qs = params.toString();
  return apiRequest(`/v1/recruitment/applications${qs ? `?${qs}` : ''}`);
}
export function createApplication(body: { candidateId: string; requisitionId: string; notes?: string }): Promise<Application> {
  return apiRequest('/v1/recruitment/applications', { method: 'POST', body });
}
export function moveApplication(id: string, body: { status?: JobApplicationStatus; stageId?: string | null; rejectionReason?: string; reason?: string }): Promise<Application> {
  return apiRequest(`/v1/recruitment/applications/${encodeURIComponent(id)}/move`, { method: 'POST', body });
}

// ── Offers (with the extend-offer gate) + convert ────────────────────────────
export function listOffers(applicationId: string): Promise<{ offers: Offer[] }> {
  return apiRequest(`/v1/recruitment/applications/${encodeURIComponent(applicationId)}/offers`);
}
export function createOffer(applicationId: string, body: { grossSalary: string; currency: string; payFrequency: string; employeeNumber?: string }): Promise<Offer> {
  return apiRequest(`/v1/recruitment/applications/${encodeURIComponent(applicationId)}/offers`, { method: 'POST', body });
}
export function offerAction(id: string, action: 'approve' | 'accept' | 'decline', body: { reason?: string } = {}): Promise<Offer> {
  return apiRequest(`/v1/recruitment/offers/${encodeURIComponent(id)}/${action}`, { method: 'POST', body });
}
export function requestExtendOffer(id: string): Promise<Offer & { resolvedApprover: ResolvedApprover }> {
  return apiRequest(`/v1/recruitment/offers/${encodeURIComponent(id)}/request-extend`, { method: 'POST', body: {} });
}
export function approveExtendOffer(id: string): Promise<Offer> {
  return apiRequest(`/v1/recruitment/offers/${encodeURIComponent(id)}/approve-extend`, { method: 'POST', body: {} });
}
export function rejectExtendOffer(id: string, body: { reason?: string } = {}): Promise<Offer> {
  return apiRequest(`/v1/recruitment/offers/${encodeURIComponent(id)}/reject-extend`, { method: 'POST', body });
}
export function convertOffer(id: string, body: { employeeNumber?: string; hireDate?: string; stateCode?: string; proceedDespiteRehireWarning?: boolean }): Promise<{ employee: { id: string; employeeNumber: string }; applicationId: string }> {
  return apiRequest(`/v1/recruitment/offers/${encodeURIComponent(id)}/convert`, { method: 'POST', body });
}
